import React from 'react';
import './SettingsRow.css';

/**
 * The one row pattern every Settings tab uses: an uppercase section label
 * above ONE card; rows inside the card separated by hairlines, each a label
 * column and a control column with the control vertically centred.
 */
export const SettingsSection: React.FC<{
  label: string;
  /** Right-aligned hint on the label line. */
  hint?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ label, hint, className, children }) => (
  <section className={`settings-section ${className || ''}`}>
    <div className="settings-section__head">
      <h3 className="settings-section__label">{label}</h3>
      {hint && <span className="settings-section__hint">{hint}</span>}
    </div>
    {children}
  </section>
);

export const SettingsCard: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className,
  children,
}) => <div className={`settings-card ${className || ''}`}>{children}</div>;

interface SettingsRowProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** The control column: a toggle, pills, a select, a button. */
  control?: React.ReactNode;
  /** Rendered under the title and description, across the label column. */
  below?: React.ReactNode;
  /** Indented and dimmed: a setting that depends on the row above being on. */
  dependent?: boolean;
  disabled?: boolean;
  className?: string;
  /** The control sits under the label instead of beside it (wide controls such as pill rows). */
  stacked?: boolean;
  children?: React.ReactNode;
}

export const SettingsRow: React.FC<SettingsRowProps> = ({
  title,
  description,
  control,
  below,
  dependent = false,
  disabled = false,
  className,
  stacked = false,
  children,
}) => (
  <div
    className={`settings-row-item ${dependent ? 'settings-row-item--dependent' : ''} ${
      disabled ? 'settings-row-item--disabled' : ''
    } ${stacked ? 'settings-row-item--stacked' : ''} ${className || ''}`}
  >
    <div className="settings-row-item__label">
      <div className="settings-row-item__title">{title}</div>
      {description && <div className="settings-row-item__desc">{description}</div>}
      {below}
    </div>
    {control !== undefined && <div className="settings-row-item__control">{control}</div>}
    {children}
  </div>
);

/** Amber status chip: reserved for Vacation Mode state. */
export const VacationChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="settings-vacation-chip">{children}</span>
);
